import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { MessageSquareText, RefreshCw } from "lucide-react";
import { Button } from "@/components/ui/button";
import { fmtDateTime } from "@/lib/datetime";

export default function SmsLogPanel() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);

  const load = () => {
    setLoading(true);
    api.get("/sms-log", { params: { limit: 20 } })
      .then((r) => setRows(r.data))
      .catch(() => setRows([]))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  // Most recent row reflects the provider currently in use. Old mock-mode
  // rows from dev should not flip the warning back on once we've upgraded.
  const isMock = rows.length > 0 && rows[0]?.provider === "mock";

  return (
    <Card className="p-5 rounded-xl border-border bg-card/80" data-testid="sms-log-panel">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h2 className="font-display font-semibold text-lg flex items-center gap-2">
            <MessageSquareText className="h-4 w-4 text-accent" /> Customer SMS log
          </h2>
          <p className="text-xs text-muted-foreground mt-0.5">
            Notifications sent when an order goes in transit or is delivered.
            {isMock && <span className="ml-2 text-destructive">Running in MOCK mode — set SMS_PROVIDER + VONAGE_API_KEY / VONAGE_API_SECRET / VONAGE_FROM_NUMBER in backend/.env to send real SMS.</span>}
          </p>
        </div>
        <Button variant="ghost" size="sm" onClick={load} disabled={loading} data-testid="sms-log-refresh">
          <RefreshCw className={`h-4 w-4 mr-1.5 ${loading ? "animate-spin" : ""}`} /> Refresh
        </Button>
      </div>

      <ul className="space-y-2 max-h-80 overflow-y-auto scrollbar-thin">
        {rows.map((r, i) => (
          <li key={i} className="flex items-start gap-3 p-3 rounded-lg bg-secondary/40 border border-border" data-testid={`sms-row-${i}`}>
            <span className={`text-[10px] uppercase tracking-wide font-medium px-2 py-0.5 rounded-full shrink-0 mt-0.5 ${
              r.status === "failed" ? "bg-destructive/15 text-destructive" :
              r.provider === "mock" ? "bg-[#3a86ff]/15 text-[#1f4ea3]" : "bg-accent/15 text-accent"
            }`}>
              {r.provider === "mock" ? "MOCK" : r.status?.toUpperCase()}
            </span>
            <div className="flex-1 min-w-0">
              <div className="text-sm font-medium font-mono-num">{r.to}</div>
              <div className="text-sm text-muted-foreground">{r.body}</div>
              <div className="text-xs text-muted-foreground/70 mt-0.5 font-mono-num">{fmtDateTime(r.created_at)}</div>
            </div>
          </li>
        ))}
        {rows.length === 0 && (
          <li className="text-sm text-muted-foreground py-6 text-center">
            No SMS sent yet. Build & start a route, then deliver an order to trigger notifications.
          </li>
        )}
      </ul>
    </Card>
  );
}
